import React, { useState } from 'react';
import { useGameStore } from '../../state/game-store.js';
import { Shield, User, Lock, Mail, X, AlertCircle, CheckCircle2, Loader2, LogIn, UserPlus } from 'lucide-react';

export const AuthModal: React.FC = () => {
  const { isAuthModalOpen, authModalMode, closeAuthModal, openAuthModal, login, register } = useGameStore();

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (!isAuthModalOpen) return null;

  const isLogin = authModalMode === 'login';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);

    if (isLogin) {
      if (!username.trim() || !password) {
        setError('Vui lòng điền đầy đủ tài khoản và mật khẩu.');
        return;
      }

      setLoading(true);
      const res = await login(username.trim(), password);
      setLoading(false);

      if (!res.success) {
        setError(res.error || 'Đăng nhập không thành công.');
      } else {
        setSuccessMsg('Đăng nhập thành công! Chào mừng Chỉ huy.');
        setTimeout(() => closeAuthModal(), 800);
      }
    } else {
      if (!username.trim() || !email.trim() || !password) {
        setError('Vui lòng điền đầy đủ thông tin đăng ký.');
        return;
      }

      if (username.trim().length < 3) {
        setError('Tên người dùng tối thiểu phải có 3 ký tự.');
        return;
      }

      if (password.length < 6) {
        setError('Mật khẩu tối thiểu phải có 6 ký tự.');
        return;
      }

      if (password !== confirmPassword) {
        setError('Xác nhận mật khẩu không khớp.');
        return;
      }

      setLoading(true);
      const res = await register(username.trim(), email.trim(), password);
      setLoading(false);

      if (!res.success) {
        setError(res.error || 'Đăng ký tài khoản thất bại.');
      } else {
        setSuccessMsg('Đăng ký tài khoản thành công! Đã tự động đăng nhập.');
        setTimeout(() => closeAuthModal(), 1000);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm animate-in fade-in duration-150">
      <div 
        className="relative w-full max-w-md bg-zinc-900/95 border border-zinc-700/80 rounded-xl shadow-2xl shadow-black/80 overflow-hidden font-sans"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Ribbon */}
        <div className="flex items-center justify-between px-6 py-4 bg-zinc-950/80 border-b border-zinc-800">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded-lg bg-olive-700/20 border border-olive-500/30 text-olive-400">
              <Shield className="w-5 h-5 text-lime-500" />
            </div>
            <div>
              <h2 className="text-base font-bold tracking-wider uppercase text-zinc-100 font-tactical">
                {isLogin ? 'Xác thực Chỉ Huy' : 'Gia nhập Chiến Trường'}
              </h2>
              <p className="text-xs text-zinc-400 font-mono">
                {isLogin ? 'COMM-AUTH // LOGIN PROTOCOL' : 'NEW RECRUIT // ENLISTMENT'}
              </p>
            </div>
          </div>
          <button
            onClick={closeAuthModal}
            className="p-1.5 text-zinc-400 hover:text-zinc-100 rounded-lg hover:bg-zinc-800/80 transition-colors"
            title="Đóng"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Selection */}
        <div className="grid grid-cols-2 p-1.5 mx-6 mt-5 bg-zinc-950 border border-zinc-800 rounded-lg">
          <button
            type="button"
            onClick={() => {
              setError(null);
              setSuccessMsg(null);
              openAuthModal('login');
            }}
            className={`flex items-center justify-center gap-2 py-2 text-xs font-semibold tracking-wider uppercase rounded-md transition-all font-tactical ${
              isLogin
                ? 'bg-zinc-800 text-lime-400 border border-zinc-700 shadow-sm'
                : 'text-zinc-400 hover:text-zinc-200'
            }`}
          >
            <LogIn className="w-3.5 h-3.5" />
            Đăng Nhập
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              setSuccessMsg(null);
              openAuthModal('register');
            }}
            className={`flex items-center justify-center gap-2 py-2 text-xs font-semibold tracking-wider uppercase rounded-md transition-all font-tactical ${
              !isLogin
                ? 'bg-zinc-800 text-lime-400 border border-zinc-700 shadow-sm'
                : 'text-zinc-400 hover:text-zinc-200'
            }`}
          >
            <UserPlus className="w-3.5 h-3.5" />
            Đăng Ký
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-start gap-2.5 p-3 text-xs bg-red-950/60 border border-red-800/80 rounded-lg text-red-200 animate-in fade-in">
              <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {successMsg && (
            <div className="flex items-start gap-2.5 p-3 text-xs bg-lime-950/60 border border-lime-800/80 rounded-lg text-lime-200 animate-in fade-in">
              <CheckCircle2 className="w-4 h-4 text-lime-400 shrink-0 mt-0.5" />
              <span>{successMsg}</span>
            </div>
          )}

          {/* Username */}
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-zinc-300 tracking-wider uppercase font-tactical">
              {isLogin ? 'Tên đăng nhập hoặc Email' : 'Tên Chỉ Huy (Callsign)'}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-zinc-500">
                <User className="w-4 h-4" />
              </div>
              <input
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={isLogin ? 'VD: Commander_Viper hoặc email' : 'VD: Commander_Ghost'}
                className="w-full pl-9 pr-3 py-2 text-sm bg-zinc-950 border border-zinc-700/80 rounded-lg text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-lime-500/80 focus:ring-1 focus:ring-lime-500/50 font-mono transition-colors"
              />
            </div>
          </div>

          {/* Email (Register only) */}
          {!isLogin && (
            <div className="space-y-1.5 animate-in fade-in">
              <label className="block text-xs font-medium text-zinc-300 tracking-wider uppercase font-tactical">
                Địa Chỉ Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-zinc-500">
                  <Mail className="w-4 h-4" />
                </div>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="commander@army.mil"
                  className="w-full pl-9 pr-3 py-2 text-sm bg-zinc-950 border border-zinc-700/80 rounded-lg text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-lime-500/80 focus:ring-1 focus:ring-lime-500/50 font-mono transition-colors"
                />
              </div>
            </div>
          )}

          {/* Password */}
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-zinc-300 tracking-wider uppercase font-tactical">
              Mật Khẩu
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-zinc-500">
                <Lock className="w-4 h-4" />
              </div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full pl-9 pr-3 py-2 text-sm bg-zinc-950 border border-zinc-700/80 rounded-lg text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-lime-500/80 focus:ring-1 focus:ring-lime-500/50 font-mono transition-colors"
              />
            </div>
          </div>

          {/* Confirm Password (Register only) */}
          {!isLogin && (
            <div className="space-y-1.5 animate-in fade-in">
              <label className="block text-xs font-medium text-zinc-300 tracking-wider uppercase font-tactical">
                Xác Nhận Mật Khẩu
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-zinc-500">
                  <Lock className="w-4 h-4" />
                </div>
                <input
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-9 pr-3 py-2 text-sm bg-zinc-950 border border-zinc-700/80 rounded-lg text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-lime-500/80 focus:ring-1 focus:ring-lime-500/50 font-mono transition-colors"
                />
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full mt-2 flex items-center justify-center gap-2 py-2.5 px-4 bg-gradient-to-r from-lime-600 to-lime-500 hover:from-lime-500 hover:to-lime-400 text-zinc-950 font-bold text-sm tracking-wider uppercase rounded-lg shadow-lg shadow-lime-900/30 font-tactical transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Đang xử lý...</span>
              </>
            ) : isLogin ? (
              <>
                <LogIn className="w-4 h-4" />
                <span>Đăng Nhập Ngay</span>
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4" />
                <span>Tạo Tài Khoản Mới</span>
              </>
            )}
          </button>
        </form>

        {/* Footer info */}
        <div className="px-6 py-3 bg-zinc-950/50 border-t border-zinc-800/80 text-center text-xs text-zinc-500 font-mono">
          {isLogin ? (
            <span>
              Chưa có tài khoản?{' '}
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  openAuthModal('register');
                }}
                className="text-lime-400 hover:underline font-semibold"
              >
                Đăng ký chỉ huy mới
              </button>
            </span>
          ) : (
            <span>
              Đã có tài khoản?{' '}
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  openAuthModal('login');
                }}
                className="text-lime-400 hover:underline font-semibold"
              >
                Đăng nhập tại đây
              </button>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
